/**
 * frontend/src/pages/NewChangeRequest.js
 *
 * Full page for creating new compliance change requests.
 * Captures title, description, change type, and impact level.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Alert } from 'react-bootstrap';
import Select from 'react-select';
import { motion } from 'framer-motion';
import {
  FiArrowLeft, FiFileText, FiSave, FiCheckCircle,
  FiAlertCircle, FiInfo, FiTarget, FiLayers,
  FiBold, FiItalic, FiUnderline, FiList, FiCode, FiUser
} from 'react-icons/fi';
import { useWorkspace } from '../context/WorkspaceContext';
import { formatErrorMessage } from '../utils/errorHandling';
import Sidebar from '../components/Layout/Sidebar';
import Header from '../components/Layout/Header';

// TipTap Editor
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';

const pageStyles = {
  pageWrapper: {
    minHeight: '100vh',
    background: '#f8fafc',
  },
  mainContent: {
    marginLeft: '72px',
    marginTop: '64px',
    minHeight: 'calc(100vh - 64px)',
    padding: '32px 48px',
  },
  contentWrapper: {
    maxWidth: '1400px',
    margin: '0 auto',
  },
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    color: '#64748b',
    textDecoration: 'none',
    fontSize: '0.9rem',
    marginBottom: '24px',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  },
  pageHeader: {
    marginBottom: '32px',
  },
  pageTitle: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  pageSubtitle: {
    color: '#64748b',
  },
  mainGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 400px',
    gap: '32px',
    alignItems: 'start',
  },
  formCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '32px',
    border: '1px solid #e2e8f0',
  },
  formSection: {
    marginBottom: '24px',
  },
  formLabel: {
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '8px',
    display: 'block',
  },
  formHint: {
    fontSize: '0.85rem',
    color: '#64748b',
    marginTop: '6px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '24px',
  },
  submitButton: {
    background: '#6366f1',
    border: 'none',
    borderRadius: '10px',
    padding: '12px 24px',
    fontWeight: '600',
    fontSize: '0.95rem',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  cancelButton: {
    background: 'transparent',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    padding: '12px 24px',
    fontWeight: '600',
    fontSize: '0.95rem',
    color: '#64748b',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '32px',
    paddingTop: '24px',
    borderTop: '1px solid #e2e8f0',
  },
  // Editor styles
  editorWrapper: {
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  editorToolbar: {
    display: 'flex',
    gap: '4px',
    padding: '8px 12px',
    borderBottom: '1px solid #e2e8f0',
    background: '#f8fafc',
    flexWrap: 'wrap',
  },
  toolbarButton: {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#64748b',
    transition: 'all 0.15s ease',
  },
  toolbarButtonActive: {
    background: '#6366f1',
    color: 'white',
  },
  toolbarDivider: {
    width: '1px',
    height: '24px',
    background: '#e2e8f0',
    margin: '4px 8px',
  },
  editorContent: {
    minHeight: '200px',
    padding: '16px',
  },
  // Right panel styles
  rightPanel: {
    position: 'sticky',
    top: '96px',
  },
  instructionsCard: {
    background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
    borderRadius: '16px',
    padding: '28px',
    color: 'white',
    position: 'relative',
    overflow: 'hidden',
  },
  instructionsTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  tipsList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  tipItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '16px',
    fontSize: '0.9rem',
    lineHeight: '1.5',
  },
  tipIcon: {
    width: '24px',
    height: '24px',
    borderRadius: '6px',
    background: 'rgba(255,255,255,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: '2px',
  },
  tipText: {
    color: 'rgba(255,255,255,0.9)',
  },
  tipHighlight: {
    color: '#a5b4fc',
    fontWeight: '500',
  },
  // Decorative shapes
  shape1: {
    position: 'absolute',
    top: '-30px',
    right: '-30px',
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    background: 'rgba(99, 102, 241, 0.3)',
    filter: 'blur(2px)',
  },
  shape2: {
    position: 'absolute',
    bottom: '-20px',
    left: '-20px',
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'rgba(139, 92, 246, 0.25)',
    filter: 'blur(2px)',
  },
  shape3: {
    position: 'absolute',
    top: '50%',
    right: '20px',
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    background: 'rgba(99, 102, 241, 0.2)',
    transform: 'rotate(45deg)',
  },
  // Impact guide card
  impactCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid #e2e8f0',
    marginTop: '20px',
  },
  impactTitle: {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  impactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
    fontSize: '0.85rem',
  },
  impactDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  impactLabel: {
    fontWeight: '600',
    minWidth: '60px',
  },
  impactDesc: {
    color: '#64748b',
  },
  loadingWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 'calc(100vh - 64px)',
  },
  // Template selector styles
  templateSection: {
    background: 'white',
    borderRadius: '16px',
    padding: '32px',
    border: '1px solid #e2e8f0',
    marginBottom: '24px',
  },
  templateHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  templateTitle: {
    fontSize: '1.2rem',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0,
  },
  templateGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: '16px',
  },
  templateCard: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '20px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  templateCardHover: {
    borderColor: '#6366f1',
    background: '#fafbff',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
  },
  templateCardName: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '8px',
  },
  templateBadge: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '6px',
    fontSize: '0.75rem',
    fontWeight: '600',
    background: '#dbeafe',
    color: '#1e40af',
  },
  skipButton: {
    background: 'transparent',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    padding: '12px 24px',
    color: '#64748b',
    fontWeight: '500',
    cursor: 'pointer',
  },
};

// Custom styles for React Select
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '48px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '1rem',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(99, 102, 241, 0.1)' : 'none',
    '&:hover': {
      border: '1px solid #cbd5e1',
    },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '1rem',
    backgroundColor: state.isSelected
      ? '#6366f1'
      : state.isFocused
      ? '#f1f5f9'
      : 'white',
    color: state.isSelected ? 'white' : '#1e293b',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#6366f1',
    },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e2e8f0',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#94a3b8',
  }),
  singleValue: (base) => ({
    ...base,
    color: '#1e293b',
  }),
};

// Change type options - must match backend ChangeType enum (grouped for better UX)
const changeTypeOptions = [
  {
    label: 'General',
    options: [
      { value: 'policy', label: 'Policy' },
      { value: 'procedure', label: 'Procedure' },
      { value: 'documentation', label: 'Documentation' },
    ]
  },
  {
    label: 'Infrastructure',
    options: [
      { value: 'infrastructure', label: 'Infrastructure' },
      { value: 'network', label: 'Network' },
      { value: 'security', label: 'Security' },
      { value: 'cloud', label: 'Cloud Resources' },
    ]
  },
  {
    label: 'DevOps',
    options: [
      { value: 'deployment', label: 'Deployment' },
      { value: 'ci_cd', label: 'CI/CD Pipeline' },
      { value: 'kubernetes', label: 'Kubernetes' },
      { value: 'docker', label: 'Docker/Containers' },
      { value: 'monitoring', label: 'Monitoring/Alerting' },
      { value: 'logging', label: 'Logging' },
    ]
  },
  {
    label: 'Database',
    options: [
      { value: 'database', label: 'Database' },
      { value: 'schema', label: 'Schema Migration' },
      { value: 'data', label: 'Data Migration' },
    ]
  },
  {
    label: 'Application',
    options: [
      { value: 'api', label: 'API' },
      { value: 'frontend', label: 'Frontend' },
      { value: 'backend', label: 'Backend' },
      { value: 'configuration', label: 'Configuration' },
      { value: 'feature_flag', label: 'Feature Flag' },
    ]
  },
];

// Impact level options
const impactLevelOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

// Editor Toolbar Component
const EditorToolbar = ({ editor }) => {
  if (!editor) return null;

  const ToolbarBtn = ({ onClick, isActive, children, title }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        ...pageStyles.toolbarButton,
        ...(isActive ? pageStyles.toolbarButtonActive : {}),
      }}
      onMouseEnter={(e) => {
        if (!isActive) e.target.style.background = '#e2e8f0';
      }}
      onMouseLeave={(e) => {
        if (!isActive) e.target.style.background = 'transparent';
      }}
    >
      {children}
    </button>
  );

  return (
    <div style={pageStyles.editorToolbar}>
      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        title="Bold"
      >
        <FiBold size={16} />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        title="Italic"
      >
        <FiItalic size={16} />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive('underline')}
        title="Underline"
      >
        <FiUnderline size={16} />
      </ToolbarBtn>

      <div style={pageStyles.toolbarDivider}></div>

      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        title="Bullet List"
      >
        <FiList size={16} />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        title="Numbered List"
      >
        <span style={{ fontSize: '14px', fontWeight: '600' }}>1.</span>
      </ToolbarBtn>

      <div style={pageStyles.toolbarDivider}></div>

      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        isActive={editor.isActive('codeBlock')}
        title="Code Block"
      >
        <FiCode size={16} />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive('blockquote')}
        title="Quote"
      >
        <span style={{ fontSize: '16px', fontWeight: '700', lineHeight: 1 }}>"</span>
      </ToolbarBtn>
    </div>
  );
};

const NewChangeRequest = () => {
  const navigate = useNavigate();
  const {
    currentWorkspace,
    members,
    templates,
    fetchWorkspaces,
    fetchWorkspace,
    fetchMembers,
    fetchTemplates,
    createChangeRequest,
    fetchChangeRequests,
  } = useWorkspace();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    change_type: 'policy',
    impact_level: 'low',
    assigned_to: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showTemplates, setShowTemplates] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // TipTap Editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Placeholder.configure({
        placeholder: 'Describe the change in detail. Include the reason for the change, what will be affected, and any relevant context...',
      }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
      setFormData(prev => ({ ...prev, description: editor.getHTML() }));
    },
    editorProps: {
      attributes: {
        style: 'outline: none; min-height: 180px;',
      },
    },
  });

  const loadWorkspace = useCallback(async () => {
    setInitialLoading(true);
    try {
      let workspaceId = currentWorkspace?.id;
      if (!workspaceId) {
        const workspaces = await fetchWorkspaces();
        if (workspaces && workspaces.length > 0) {
          workspaceId = workspaces[0].id;
          await fetchWorkspace(workspaceId);
        } else {
          navigate('/dashboard');
          return;
        }
      }
      // Fetch members for assignee dropdown
      await fetchMembers(workspaceId);
      // Fetch templates for template selection
      await fetchTemplates(workspaceId);
    } catch (err) {
      console.error('Failed to load workspace:', err);
      navigate('/dashboard');
    } finally {
      setInitialLoading(false);
    }
  }, [currentWorkspace?.id, fetchWorkspaces, fetchWorkspace, fetchMembers, fetchTemplates, navigate]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const applyTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData({
      ...formData,
      title: template.title_template || '',
      change_type: template.change_type,
    });
    editor?.commands.setContent(template.description_template || '');
    setShowTemplates(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentWorkspace?.id) return;

    // Validate description has content
    const descriptionText = editor?.getText()?.trim() || '';
    if (!descriptionText) {
      setError('Please provide a description for the change request');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const requestData = {
        ...formData,
        assigned_to: formData.assigned_to ? parseInt(formData.assigned_to) : null,
      };
      await createChangeRequest(currentWorkspace.id, requestData);
      await fetchChangeRequests(currentWorkspace.id);
      navigate('/dashboard');
    } catch (err) {
      setError(formatErrorMessage(err, 'Failed to create change request'));
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div style={pageStyles.pageWrapper}>
        <Sidebar workspace={currentWorkspace} />
        <Header workspace={currentWorkspace} />
        <main style={{ ...pageStyles.mainContent, ...pageStyles.loadingWrapper }}>
          <div className="spinner" style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e2e8f0',
            borderTopColor: '#6366f1',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </main>
      </div>
    );
  }

  return (
    <div style={pageStyles.pageWrapper}>
      <Sidebar workspace={currentWorkspace} />
      <Header workspace={currentWorkspace} />

      <main style={pageStyles.mainContent}>
        <div style={pageStyles.contentWrapper}>
          <motion.button
            style={pageStyles.backButton}
            onClick={() => navigate('/dashboard')}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ color: '#1e293b' }}
          >
            <FiArrowLeft />
            Back to Dashboard
          </motion.button>

          <motion.div
            style={pageStyles.pageHeader}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 style={pageStyles.pageTitle}>
              <FiFileText />
              New Change Request
            </h1>
            <p style={pageStyles.pageSubtitle}>
              Create a new compliance change request for your team to review
            </p>
          </motion.div>

          {/* Template Selector */}
          {showTemplates && templates.length > 0 && (
            <motion.div
              style={pageStyles.templateSection}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
            >
              <div style={pageStyles.templateHeader}>
                <h2 style={pageStyles.templateTitle}>Start with a Template</h2>
                <button
                  style={pageStyles.skipButton}
                  onClick={() => setShowTemplates(false)}
                >
                  Skip - Start from scratch
                </button>
              </div>
              <div style={pageStyles.templateGrid}>
                {templates.map((template) => (
                  <motion.div
                    key={template.id}
                    style={pageStyles.templateCard}
                    onClick={() => applyTemplate(template)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <div style={pageStyles.templateCardName}>{template.name}</div>
                    {template.is_default && (
                      <span style={pageStyles.templateBadge}>Default</span>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          <div style={pageStyles.mainGrid}>
            {/* Form Section */}
            <motion.div
              style={pageStyles.formCard}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <div style={pageStyles.formSection}>
                  <Form.Label style={pageStyles.formLabel}>Title</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    placeholder="Enter a descriptive title for the change"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    size="lg"
                  />
                  <p style={pageStyles.formHint}>
                    A clear, concise title that summarizes the change
                  </p>
                </div>

                <div style={pageStyles.formSection}>
                  <Form.Label style={pageStyles.formLabel}>Description</Form.Label>
                  <div style={pageStyles.editorWrapper}>
                    <EditorToolbar editor={editor} />
                    <div style={pageStyles.editorContent}>
                      <EditorContent editor={editor} />
                    </div>
                  </div>
                  <p style={pageStyles.formHint}>
                    Provide enough detail for reviewers to understand and evaluate the change
                  </p>
                </div>

                <div style={pageStyles.formGrid}>
                  <div style={pageStyles.formSection}>
                    <Form.Label style={pageStyles.formLabel}>Change Type</Form.Label>
                    <Select
                      options={changeTypeOptions}
                      value={
                        changeTypeOptions
                          .flatMap(group => group.options)
                          .find(opt => opt.value === formData.change_type)
                      }
                      onChange={(option) => setFormData({ ...formData, change_type: option.value })}
                      styles={selectStyles}
                      placeholder="Select change type"
                      isSearchable
                    />
                  </div>

                  <div style={pageStyles.formSection}>
                    <Form.Label style={pageStyles.formLabel}>Impact Level</Form.Label>
                    <Select
                      options={impactLevelOptions}
                      value={impactLevelOptions.find(opt => opt.value === formData.impact_level)}
                      onChange={(option) => setFormData({ ...formData, impact_level: option.value })}
                      styles={selectStyles}
                      placeholder="Select impact level"
                      isSearchable
                    />
                  </div>

                  <div style={pageStyles.formSection}>
                    <Form.Label style={pageStyles.formLabel}>
                      <FiUser style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                      Assign To
                    </Form.Label>
                    <Select
                      options={[
                        { value: '', label: 'Unassigned' },
                        ...members.map((member) => ({
                          value: member.user_id,
                          label: member.user_name,
                        }))
                      ]}
                      value={
                        formData.assigned_to
                          ? members.map((member) => ({
                              value: member.user_id,
                              label: member.user_name,
                            })).find(opt => opt.value === formData.assigned_to)
                          : { value: '', label: 'Unassigned' }
                      }
                      onChange={(option) => setFormData({ ...formData, assigned_to: option.value })}
                      styles={selectStyles}
                      placeholder="Select assignee"
                      isClearable
                      isSearchable
                    />
                  </div>
                </div>

                <div style={pageStyles.buttonGroup}>
                  <Button
                    style={pageStyles.cancelButton}
                    onClick={() => navigate('/dashboard')}
                    type="button"
                  >
                    Cancel
                  </Button>
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      type="submit"
                      style={pageStyles.submitButton}
                      disabled={loading}
                    >
                      {loading ? (
                        'Creating...'
                      ) : (
                        <>
                          <FiSave />
                          Create Request
                        </>
                      )}
                    </Button>
                  </motion.div>
                </div>
              </Form>
            </motion.div>

            {/* Right Panel - Instructions */}
            <motion.div
              style={pageStyles.rightPanel}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              {/* Tips Card */}
              <div style={pageStyles.instructionsCard}>
                {/* Decorative shapes */}
                <div style={pageStyles.shape1}></div>
                <div style={pageStyles.shape2}></div>
                <div style={pageStyles.shape3}></div>

                <h3 style={pageStyles.instructionsTitle}>
                  <FiInfo size={18} />
                  Tips for a Great Request
                </h3>

                <ul style={pageStyles.tipsList}>
                  <li style={pageStyles.tipItem}>
                    <div style={pageStyles.tipIcon}>
                      <FiCheckCircle size={14} />
                    </div>
                    <div>
                      <span style={pageStyles.tipHighlight}>Be specific</span>
                      <span style={pageStyles.tipText}> - Clearly state what is changing and why it matters</span>
                    </div>
                  </li>
                  <li style={pageStyles.tipItem}>
                    <div style={pageStyles.tipIcon}>
                      <FiTarget size={14} />
                    </div>
                    <div>
                      <span style={pageStyles.tipHighlight}>Include context</span>
                      <span style={pageStyles.tipText}> - Explain the business need driving this change</span>
                    </div>
                  </li>
                  <li style={pageStyles.tipItem}>
                    <div style={pageStyles.tipIcon}>
                      <FiAlertCircle size={14} />
                    </div>
                    <div>
                      <span style={pageStyles.tipHighlight}>Note dependencies</span>
                      <span style={pageStyles.tipText}> - List any systems or processes affected</span>
                    </div>
                  </li>
                  <li style={pageStyles.tipItem}>
                    <div style={pageStyles.tipIcon}>
                      <FiLayers size={14} />
                    </div>
                    <div>
                      <span style={pageStyles.tipHighlight}>Set proper impact</span>
                      <span style={pageStyles.tipText}> - Higher impact triggers more thorough review</span>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Impact Level Guide */}
              <div style={pageStyles.impactCard}>
                <h4 style={pageStyles.impactTitle}>
                  <FiAlertCircle size={16} />
                  Impact Level Guide
                </h4>

                <div style={pageStyles.impactItem}>
                  <div style={{ ...pageStyles.impactDot, background: '#10b981' }}></div>
                  <span style={{ ...pageStyles.impactLabel, color: '#10b981' }}>Low</span>
                  <span style={pageStyles.impactDesc}>Minor updates, no workflow changes</span>
                </div>

                <div style={pageStyles.impactItem}>
                  <div style={{ ...pageStyles.impactDot, background: '#f59e0b' }}></div>
                  <span style={{ ...pageStyles.impactLabel, color: '#f59e0b' }}>Medium</span>
                  <span style={pageStyles.impactDesc}>Affects team processes</span>
                </div>

                <div style={pageStyles.impactItem}>
                  <div style={{ ...pageStyles.impactDot, background: '#f97316' }}></div>
                  <span style={{ ...pageStyles.impactLabel, color: '#f97316' }}>High</span>
                  <span style={pageStyles.impactDesc}>Cross-team or customer impact</span>
                </div>

                <div style={{ ...pageStyles.impactItem, marginBottom: 0 }}>
                  <div style={{ ...pageStyles.impactDot, background: '#ef4444' }}></div>
                  <span style={{ ...pageStyles.impactLabel, color: '#ef4444' }}>Critical</span>
                  <span style={pageStyles.impactDesc}>Organization-wide changes</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      {/* Editor Styles */}
      <style>{`
        .ProseMirror {
          outline: none;
          min-height: 180px;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #94a3b8;
          pointer-events: none;
          height: 0;
        }
        .ProseMirror p {
          margin-bottom: 0.75em;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5em;
          margin-bottom: 0.75em;
        }
        .ProseMirror li {
          margin-bottom: 0.25em;
        }
        .ProseMirror blockquote {
          border-left: 3px solid #6366f1;
          padding-left: 1em;
          margin-left: 0;
          color: #64748b;
          font-style: italic;
        }
        .ProseMirror pre {
          background: #1e293b;
          color: #e2e8f0;
          padding: 12px 16px;
          border-radius: 8px;
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 0.875rem;
          overflow-x: auto;
        }
        .ProseMirror code {
          background: #f1f5f9;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 0.875em;
        }
        .ProseMirror pre code {
          background: none;
          padding: 0;
        }
      `}</style>
    </div>
  );
};

export default NewChangeRequest;
